import { useState, useEffect, useRef } from 'react';
import { api } from '../api.js';
import { NotePhotoPicker } from './NotePhotoPicker.jsx';
import { uploadNotePhotos } from '../note-photos.js';
import { useConfig } from '../config.js';
import { CoralMark } from './CoralMark.jsx';

// Floating feedback button + modal. The `page` prop is the key value for
// improvement: it's submitted with the message so we know where the user
// was. Users also choose a kind (bug/idea/question) and can attach up to
// six screenshots. The modal auto-captures viewport/theme/user-agent as
// invisible metadata so admin has real signal for triage. Kinds + severities
// come from app_config `feedback_taxonomy` so admin can edit them without
// a redeploy.
// The header's feedback icon (Header.jsx) opens this window on a phone,
// where the floating button is docked away.
export const OPEN_FEEDBACK_EVENT = 'aqs:open-feedback';

export function FeedbackButton({ page, userEmail, dockOnPhone = false }) {
  const taxonomy = useConfig()?.feedback;
  const kinds = taxonomy?.kinds || [
    { k: 'bug', label: 'Bug', hint: 'Something is broken' },
    { k: 'idea', label: 'Idea', hint: 'Feature request or improvement' },
    { k: 'question', label: 'Question', hint: 'How do I…? or why does…?' },
  ];
  const severities = taxonomy?.severities || [
    { k: 'nice-to-have', label: 'Nice', hint: 'Nice to have, no rush', dot: 'var(--text-muted)' },
    { k: 'annoying', label: 'Annoying', hint: 'Annoying but workable', dot: '#ffc166' },
    { k: 'blocking', label: 'Blocking', hint: 'Blocking me from using it', dot: 'var(--alert)' },
  ];
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(OPEN_FEEDBACK_EVENT, show);
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, show);
  }, []);
  const [status, setStatus] = useState('idle'); // idle | sending | sent
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [kind, setKind] = useState('bug'); // bug | idea | question
  const [severity, setSeverity] = useState('annoying'); // nice-to-have | annoying | blocking
  const [photos, setPhotos] = useState([]);
  const [photoWarn, setPhotoWarn] = useState(''); // surfaces attachment-upload failure in the success screen
  const [err, setErr] = useState('');
  const textRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => textRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && open) setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (trimmed.length < 3) return;
    setStatus('sending');
    setErr('');
    setPhotoWarn('');
    try {
      // Auto-captured device context — invisible to the user, real signal
      // for triage. Theme is read off the <html> data-theme attribute that
      // the inline script sets before React mounts.
      const vw = window.innerWidth;
      const viewport = vw < 640 ? 'mobile' : vw < 1024 ? 'tablet' : 'desktop';
      const theme = document.documentElement.getAttribute('data-theme') || null;

      // Signed-in users get their session email attached server-side;
      // only send the explicit field when they typed one (shouldn't happen
      // when userEmail is present — we hide that input).
      const payload = {
        message: trimmed,
        screen: page ? `app:${page}` : 'app:unknown',
        kind,
        severity,
        viewport: `${viewport}·${vw}x${window.innerHeight}`,
        theme,
      };
      if (!userEmail && email.trim()) payload.email = email.trim();

      const r = await api('/api/feedback', { method: 'POST', body: payload });
      // Screenshots go up one per request, bound to the new feedback id,
      // canvas-first like every upload (an iPhone ships a small JPEG, not
      // raw HEIC). A failed one never blocks the text, which already landed.
      let warn = '';
      if (photos.length && r.id) {
        const photoErrors = [];
        const failed = await uploadNotePhotos(
          `/api/feedback/${r.id}/attachment?t=${encodeURIComponent(r.upload_token || '')}`,
          photos,
          photoErrors
        );
        if (failed) {
          warn = 'failed';
          const what =
            failed === photos.length
              ? photos.length === 1
                ? "The screenshot didn't upload"
                : "The screenshots didn't upload"
              : `${failed} of ${photos.length} screenshots didn't upload`;
          setPhotoWarn(photoErrors[0] ? `${what}. ${photoErrors[0]}` : what);
        }
      }
      setStatus('sent');
      setTimeout(
        () => {
          setOpen(false);
          setMessage('');
          setEmail('');
          setPhotos([]);
          setKind('bug');
          setSeverity('annoying');
          setPhotoWarn('');
          setStatus('idle');
        },
        warn ? 4000 : 2000
      );
    } catch (e) {
      setStatus('idle');
      setErr(e.message || 'Could not send. Try again?');
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        className={`font-heading fb-trigger${dockOnPhone ? ' fb-docked' : ''}`}
      >
        {/* A chat-bubble glyph reads as "feedback" in every locale; the
            old kanji 侍 was being read as a language marker rather than
            our Sensei brand mark, so users couldn't tell what the button
            did without tapping it. */}
        <svg
          className="fb-trigger-icon"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
        <span className="fb-trigger-label">Feedback</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="fb-title"
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            background: 'rgba(8, 20, 54, 0.72)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="fade-in"
            style={{
              width: '100%',
              maxWidth: 460,
              padding: '26px 24px 28px',
              background: 'var(--sheet-bg)',
              border: '1px solid var(--cyan)',
              borderRadius: 22,
              position: 'relative',
              maxHeight: 'calc(100vh - 40px)',
              overflowY: 'auto',
            }}
          >
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 44,
                height: 44,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-dim)',
                fontSize: 24,
                lineHeight: 1,
              }}
            >
              ×
            </button>
            {status === 'sent' ? (
              <div style={{ textAlign: 'center', padding: '6px 0' }}>
                <CoralMark size={40} style={{ marginBottom: 2 }} />
                <div
                  className="font-heading"
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.3em',
                    color: 'var(--text-dim)',
                    marginBottom: 10,
                  }}
                >
                  SENSEI
                </div>
                <h3
                  id="fb-title"
                  className="font-heading"
                  style={{ fontSize: 20, marginBottom: 6 }}
                >
                  Got it — thanks.
                </h3>
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.55 }}>
                  A human will read it.{' '}
                  {userEmail || email.trim()
                    ? 'Check your inbox for a confirmation.'
                    : "Add your email next time if you'd like a reply."}
                </p>
                {photoWarn && (
                  <p
                    role="alert"
                    style={{ fontSize: 13, color: 'var(--alert)', lineHeight: 1.5, marginTop: 10 }}
                  >
                    {photoWarn}. Your message was sent
                    {userEmail || email.trim() ? '; reply to our email with the rest.' : '.'}
                  </p>
                )}
              </div>
            ) : (
              <form onSubmit={submit}>
                <h3
                  id="fb-title"
                  className="font-heading"
                  style={{ fontSize: 20, letterSpacing: '0.02em', marginBottom: 6 }}
                >
                  Tell Sensei
                </h3>
                <p
                  style={{
                    fontSize: 14,
                    color: 'var(--text-muted)',
                    lineHeight: 1.55,
                    marginBottom: 14,
                  }}
                >
                  Quick note on{' '}
                  <strong style={{ color: 'var(--cyan)' }}>{page || 'this page'}</strong>. What
                  kind?
                </p>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {kinds.map((k) => {
                    const active = k.k === kind;
                    return (
                      <button
                        key={k.k}
                        type="button"
                        onClick={() => setKind(k.k)}
                        title={k.hint}
                        className="font-heading"
                        aria-pressed={active}
                        style={{
                          padding: '0 14px',
                          minHeight: 44,
                          borderRadius: 999,
                          background: active ? 'var(--cyan)' : 'transparent',
                          color: active ? 'var(--cyan-text)' : 'var(--text-muted)',
                          border: `1px solid ${active ? 'var(--cyan)' : 'var(--surface-border)'}`,
                          fontSize: 13,
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {k.label}
                      </button>
                    );
                  })}
                </div>

                <div
                  className="font-heading"
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: 'var(--text-dim)',
                    marginBottom: 6,
                  }}
                >
                  How bad?
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                  {severities.map((s) => {
                    const active = s.k === severity;
                    return (
                      <button
                        key={s.k}
                        type="button"
                        onClick={() => setSeverity(s.k)}
                        title={s.hint}
                        className="font-heading"
                        aria-pressed={active}
                        style={{
                          padding: '0 12px',
                          minHeight: 44,
                          borderRadius: 999,
                          background: active ? 'var(--surface)' : 'transparent',
                          color: active ? 'var(--text)' : 'var(--text-muted)',
                          border: `1px solid ${active ? s.dot : 'var(--surface-border)'}`,
                          fontSize: 12,
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            background: s.dot,
                            display: 'inline-block',
                          }}
                        />
                        {s.label}
                      </button>
                    );
                  })}
                </div>

                <textarea
                  ref={textRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    kind === 'bug'
                      ? 'What broke? What were you doing?'
                      : kind === 'idea'
                        ? 'What would make TankSensei better?'
                        : 'What do you want to know?'
                  }
                  required
                  aria-required="true"
                  style={{
                    width: '100%',
                    minHeight: 110,
                    padding: '10px 12px',
                    background: 'transparent',
                    color: 'var(--text)',
                    border: '1px solid var(--surface-border)',
                    borderRadius: 12,
                    outline: 'none',
                    fontSize: 16,
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                />

                {/* Screenshots */}
                <div style={{ marginTop: 10 }}>
                  <NotePhotoPicker
                    files={photos}
                    onChange={setPhotos}
                    disabled={status === 'sending'}
                    label={photos.length ? '+ Screenshot' : '+ Screenshots (optional)'}
                  />
                </div>

                {userEmail ? null : (
                  <>
                    <div
                      className="font-heading"
                      style={{
                        fontSize: 11,
                        letterSpacing: '0.22em',
                        textTransform: 'uppercase',
                        color: 'var(--text-dim)',
                        marginTop: 14,
                        marginBottom: 4,
                      }}
                    >
                      Email{' '}
                      <span style={{ color: 'var(--text-dim)' }}>(optional, for a reply)</span>
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      style={{
                        width: '100%',
                        background: 'transparent',
                        color: 'var(--text)',
                        border: 'none',
                        borderBottom: '1px solid var(--surface-border)',
                        padding: '8px 0',
                        fontSize: 16,
                        outline: 'none',
                      }}
                    />
                  </>
                )}
                {err && (
                  <p role="alert" style={{ fontSize: 14, color: 'var(--alert)', marginTop: 10 }}>
                    {err}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={status === 'sending' || message.trim().length < 3}
                  className="font-heading"
                  style={{
                    marginTop: 18,
                    width: '100%',
                    padding: '12px 0',
                    borderRadius: 12,
                    background: 'var(--cyan)',
                    color: 'var(--cyan-text)',
                    fontWeight: 600,
                    fontSize: 14,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    opacity: status === 'sending' || message.trim().length < 3 ? 0.5 : 1,
                  }}
                >
                  {status === 'sending' ? 'Sending…' : 'Send'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
